// The tasks waiting for the operator and the ones at work, for every surface
// outside the Tasks page: Home's tile and Working now, the sidebar and the
// phone's More badges, Today's Needs you and Working now (UX review H1, H2).
// One list read (api/tasks LIVE_TASKS_QUERY) under one key, so they share a
// request; only where Tasks are switched on (the same flag as the nav item;
// until the settings answer, the switch this browser remembered, so the
// read starts with the first wave) and never for a child's login (an
// operator route).

import { useMemo } from "react";

import { useCustomerSettings } from "@/api/customerSettings";
import { LIVE_TASKS_QUERY, useTasks, type TaskSummary } from "@/api/tasks";
import { useRememberedNav } from "@/features/navigation/useNavFlags";
import { useAuth } from "@/stores/auth";

import { tasksNow } from "./taskModel";

export interface TasksNow {
  /** Tasks are switched on for this workspace. */
  enabled: boolean;
  /** Waiting for the operator: an OK, or an answer that did not reach its asker. */
  needsYou: number;
  /** Waiting for the operator's OK, oldest first. */
  awaiting: TaskSummary[];
  /** At work (queued, working, waiting for an answer, paused), oldest first. */
  working: TaskSummary[];
  /** The list has been read. */
  loaded: boolean;
  isLoading: boolean;
  /** The list (or the workspace settings that say whether Tasks are on)
   *  could not be read and there is nothing to show, so a zero may be a failed load. */
  isError: boolean;
  /** Read the list again (nothing while Tasks are off). */
  refetch: () => void;
}

/** `poll`: a badge reads slowly (60 s); a screen that shows the rows keeps the page's cadence. */
export function useTasksNow(customerId: string | null | undefined, { poll }: { poll?: number } = {}): TasksNow {
  const isChild = useAuth((s) => s.role) === "family";
  const op = isChild ? null : customerId || null;
  const settings = useCustomerSettings(op);
  const remembered = useRememberedNav(op)?.flags;
  // A wrong remembered "on" costs one harmless read: with Tasks off the list
  // answers `offered: false`, and the live switch then turns it off.
  const enabled = settings.data ? settings.data.tasks_enabled === true : remembered?.tasks_enabled === true;
  const q = useTasks(enabled ? op : null, LIVE_TASKS_QUERY, { poll });
  const split = useMemo(() => tasksNow(q.data), [q.data]);
  const settingsFailed = settings.isError && settings.data === undefined;
  return {
    enabled,
    ...split,
    loaded: !!q.data,
    // A remembered switch counts as known: Home and Today then draw the
    // tasks as soon as the list is read, without waiting for settings.
    isLoading: (settings.isLoading && remembered?.tasks_enabled === undefined) || (enabled && q.isLoading),
    // A settings read that failed is an error too: Tasks may well be on,
    // so "nothing waits" would be a guess.
    isError: settingsFailed || (enabled && q.isError && q.data === undefined),
    refetch: () => {
      if (settingsFailed) void settings.refetch();
      if (enabled) void q.refetch();
    },
  };
}
